import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, MessageSquareHeart, HandHeart, ArrowRight, Award } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/Site";
import { api, errMsg } from "../lib/api";

const STEPS = [
  ["Check your coverage", "Answer a few plain questions. See what respite funding you may qualify for.", "/img/step1.webp"],
  ["Choose your person", "A neighbor, a cousin, a church friend. Someone you already trust. We help you ask.", "/img/step2.webp"],
  ["Take your break", "They get a clear brief and get paid. You get a warm report after. And some rest.", "/img/step3.webp"],
];

function Waitlist() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/waitlist", { email });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="hf-card">
      <p className="hf-eyebrow">For families</p>
      <h3 className="mt-1 font-heading text-2xl font-semibold">Join the waitlist</h3>
      <p className="mt-2 text-base text-hf-muted">We'll write when Handoff opens near you. No spam, ever.</p>
      {done ? (
        <p data-testid="waitlist-success" className="mt-5 rounded-xl bg-hf-sage px-4 py-3 text-lg text-hf-green">You're on the list. Thank you.</p>
      ) : (
        <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
          <input data-testid="waitlist-email-input" type="email" required className="hf-input" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button data-testid="waitlist-submit-button" className="hf-btn hf-btn-primary">Keep me posted</button>
        </form>
      )}
    </div>
  );
}

function PartnerForm() {
  const [f, setF] = useState({ name: "", organization: "", email: "", message: "" });
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/partner-contact", f);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="hf-card bg-hf-greendark text-hf-onprimary" style={{ borderColor: "transparent" }}>
      <p className="text-sm font-semibold uppercase tracking-[0.14em] text-hf-sun">For programs and providers</p>
      <h3 className="mt-1 font-heading text-2xl font-semibold">Pilot partners wanted</h3>
      <p className="mt-2 text-base text-hf-cream/85">
        Are you a GUIDE program, health plan, or licensed respite provider? We'd love to talk about a pilot.
      </p>
      {done ? (
        <p data-testid="partner-contact-success" className="mt-5 rounded-xl bg-hf-cream px-4 py-3 text-lg text-hf-green">Thank you. We'll be in touch soon.</p>
      ) : (
        <form onSubmit={submit} className="mt-5 grid gap-3">
          <input data-testid="partner-name-input" required className="hf-input text-hf-ink" placeholder="Your name" value={f.name} onChange={set("name")} />
          <input data-testid="partner-org-input" required className="hf-input text-hf-ink" placeholder="Organization" value={f.organization} onChange={set("organization")} />
          <input data-testid="partner-email-input" type="email" required className="hf-input text-hf-ink" placeholder="Work email" value={f.email} onChange={set("email")} />
          <textarea data-testid="partner-message-input" className="hf-input min-h-[90px] text-hf-ink" placeholder="What would you like to explore?" value={f.message} onChange={set("message")} />
          <button data-testid="partner-submit-button" className="hf-btn hf-btn-coral">Start a conversation</button>
        </form>
      )}
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-hf-cream">
      <SiteHeader />

      <section className="mx-auto grid max-w-5xl gap-10 px-5 pb-16 pt-6 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:pt-14">
        <div className="hf-rise">
          <Link to="/story" data-testid="landing-accelerator-badge" className="mb-4 inline-flex max-w-full items-start gap-2 rounded-full border-[1.5px] border-hf-line bg-white px-3.5 py-1.5 text-[12.5px] font-semibold leading-snug text-hf-green transition-colors hover:border-hf-green sm:items-center sm:text-[13.5px]">
            <Award className="mt-px h-4 w-4 shrink-0 text-hf-coral sm:mt-0" /> 1st of 206 projects, AI Squads HelpBnk Accelerator, July 2026
          </Link>
          <p className="hf-eyebrow">Taking a break is caregiving, too.</p>
          <h1 data-testid="landing-headline" className="mt-4 font-heading text-4xl leading-[1.08] text-hf-ink sm:text-5xl lg:text-6xl">
            You've earned a break. It might already be paid for.
          </h1>
          <p data-testid="landing-subhead" className="mt-6 max-w-xl text-lg leading-relaxed text-hf-muted sm:text-xl">
            Handoff helps family caregivers use respite funding they may qualify for, with someone they already trust.
          </p>
          <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <button data-testid="cta-check-coverage" onClick={() => nav("/check")} className="hf-btn hf-btn-primary">
              Check if your break is covered <ArrowRight className="h-5 w-5" />
            </button>
            <button data-testid="cta-see-demo" onClick={() => nav("/demo")} className="hf-arrow min-h-[48px] px-1 text-[15px] font-semibold text-hf-green hover:underline hover:underline-offset-4">
              See the demo
            </button>
          </div>
          <p className="mt-5 text-base text-hf-muted">Free for families. No login needed to check.</p>
        </div>
        <div className="relative hf-rise" style={{ animationDelay: "120ms" }}>
          <img src="/img/hero.webp" alt="A caregiver rests on the porch while a neighbor keeps her father company" className="w-full rounded-xl border-[1.5px] border-hf-line object-cover" />
        </div>
      </section>

      <section className="bg-hf-greendark text-hf-onprimary">
        <div className="mx-auto grid max-w-5xl gap-8 px-5 py-16 sm:px-8 md:grid-cols-[auto_1fr] md:items-center">
          <p data-testid="stat-85" className="font-heading text-7xl leading-none text-hf-sun sm:text-8xl">85%</p>
          <div>
            <p className="font-heading text-2xl leading-snug sm:text-3xl">of family caregivers never use formal respite services.</p>
            <p data-testid="stat-source" className="mt-3 text-base text-hf-cream/75">Source: Utz, Journal of the American Medical Directors Association, 2021.</p>
            <p className="mt-6 max-w-2xl text-lg text-hf-cream/90">
              Respite funding already exists. Families rarely use it. They don't know it's there, and it feels easier to do it all yourself than to explain everything.
            </p>
            <p data-testid="brand-line" className="mt-6 font-heading text-xl italic text-hf-cream">Handoff keeps what you know, so someone you trust can step in.</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-20 sm:px-8">
        <p className="hf-eyebrow">How it works</p>
        <h2 className="mt-2 max-w-xl font-heading text-3xl sm:text-4xl">Three small steps to a real break.</h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {STEPS.map(([t, d, img], i) => (
            <div key={t} data-testid={`how-step-${i + 1}`} className="hf-rise" style={{ animationDelay: `${i * 90}ms` }}>
              <img src={img} alt={t} className="aspect-square w-full rounded-xl border-[1.5px] border-hf-line object-cover" />
              <p className="mt-5 text-[13px] font-semibold uppercase tracking-[0.12em] text-hf-coraldark">Step {i + 1}</p>
              <h3 className="mt-1 font-heading text-2xl">{t}.</h3>
              <p className="mt-2 text-base leading-relaxed text-hf-muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y-[1.5px] border-hf-line bg-hf-creamdeep">
        <div className="mx-auto grid max-w-5xl gap-6 px-5 py-16 sm:px-8 md:grid-cols-3">
          {[
            [HandHeart, "No app, no login", "The person stepping in just opens a text link."],
            [ShieldCheck, "You approve everything", "Nothing is shared until you say so."],
            [MessageSquareHeart, "Families pay nothing", "Funding programs pay for verified visits. Your helper gets paid."],
          ].map(([Icon, t, d]) => (
            <div key={t} className="hf-card">
              <Icon className="h-7 w-7 text-hf-green" />
              <h3 className="mt-4 font-heading text-xl">{t}</h3>
              <p className="mt-2 text-base text-hf-muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-5xl gap-6 px-5 py-20 sm:px-8 md:grid-cols-2">
        <Waitlist />
        <PartnerForm />
      </section>

      <SiteFooter />
    </div>
  );
}
